<template>
  <div class="card card-flush mb-5">
    <div class="card-header border-0 pt-6">
      <h3 class="card-title fw-bolder d-flex align-items-center gap-2">
        <i class="bi bi-whatsapp text-success fs-2"></i> {{ title }}
      </h3>
      <div class="card-toolbar gap-2" v-if="loaded">
        <span class="badge" :class="setting.configured ? 'badge-light-success' : 'badge-light-warning'">
          {{ setting.configured ? 'Configuré' : 'Non configuré' }}
        </span>
        <span v-if="setting.configured" class="badge" :class="setting.enabled ? 'badge-light-primary' : 'badge-light'">
          {{ setting.enabled ? 'Actif' : 'Désactivé' }}
        </span>
        <span v-if="setting.last_test_at" class="badge" :class="setting.last_test_ok ? 'badge-light-success' : 'badge-light-danger'">
          Dernier test {{ setting.last_test_ok ? 'réussi' : 'échoué' }} · {{ setting.last_test_at }}
        </span>
      </div>
    </div>

    <div class="card-body pt-2">
      <p v-if="description" class="text-muted">{{ description }}</p>
      <slot name="notice" :data="data"></slot>

      <!-- surveillance des envois (vérifiée toutes les 10 min, alerte par e-mail si hors service) -->
      <div v-if="loaded && setting.configured && health" class="wa-health d-flex align-items-start gap-3 rounded p-4 mb-5"
           :class="health.state === 'down' ? 'bg-light-danger' : (health.state === 'ok' ? 'bg-light-success' : 'bg-light')">
        <span class="wa-health__dot" :class="'is-' + health.state"></span>
        <div class="flex-grow-1">
          <div class="fw-bold">
            {{ health.state === 'down' ? 'Hors service : les messages WhatsApp ne partent pas'
              : (health.state === 'ok' ? 'Opérationnel' : 'Pas encore vérifié') }}
          </div>
          <div class="text-muted fs-7">
            <span v-if="health.session_status">Session WaaConnect : <strong>{{ sessionLabel(health.session_status) }}</strong></span>
            <span v-if="health.session_checked_at"> · vérifiée {{ ago(health.session_checked_at) }}</span>
          </div>
          <div class="text-muted fs-7">
            <span v-if="health.last_success_at">Dernier envoi réussi {{ ago(health.last_success_at) }}</span>
            <span v-if="health.consecutive_failures"> · <span class="text-danger">{{ health.consecutive_failures }} échec(s) d'affilée</span></span>
          </div>
          <div v-if="health.state === 'down' && health.last_error" class="text-danger fs-7 mt-1">Dernière erreur : {{ health.last_error }}</div>
          <div v-if="health.state === 'down'" class="fs-7 mt-2">
            Vérifiez dans WaaConnect que la session est connectée (rescannez le QR code si besoin) et que l'identifiant de session ci-dessous est le bon, puis envoyez un message test.
          </div>
        </div>
      </div>

      <template v-if="loaded && !readonly">
        <div class="row g-4">
          <div class="col-md-6">
            <label class="form-label" :class="{ required: !setting.configured }">Clé API WaaConnect</label>
            <input type="password" v-model="form.api_key" class="form-control" autocomplete="new-password"
                   :placeholder="setting.api_key_masked || ''" />
            <div class="form-text" v-if="setting.configured">Laissez vide pour conserver la clé actuelle.</div>
          </div>
          <div class="col-md-6">
            <label class="form-label required">Session ID</label>
            <input type="text" v-model="form.session_id" class="form-control" />
          </div>
          <div class="col-md-6">
            <label class="form-label">Numéro WhatsApp (affichage)</label>
            <input type="text" v-model="form.sender_phone" class="form-control" placeholder="+229 01 00 00 00 00" />
          </div>
          <div class="col-md-6 d-flex align-items-end">
            <div class="form-check form-switch form-check-custom form-check-solid mb-3">
              <input class="form-check-input" type="checkbox" v-model="form.enabled" id="whatsapp-enabled" />
              <label class="form-check-label" for="whatsapp-enabled">Utiliser ce numéro pour l'envoi</label>
            </div>
          </div>
          <div class="col-12 text-end">
            <button class="btn btn-primary" :disabled="saving" @click="save">
              {{ saving ? 'Enregistrement…' : 'Enregistrer' }}
            </button>
          </div>
        </div>

        <div class="separator separator-dashed my-6"></div>

        <h5 class="fw-bold mb-3">Envoyer un message test</h5>
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label class="form-label">Indicatif</label>
            <input type="text" v-model="testForm.phone_code" class="form-control" placeholder="+229" />
          </div>
          <div class="col-md-5">
            <label class="form-label">Numéro destinataire</label>
            <input type="text" v-model="testForm.phone" class="form-control" placeholder="0100000000" />
          </div>
          <div class="col-md-4">
            <button class="btn btn-light-success w-100" :disabled="testing || !setting.configured" @click="sendTest">
              <i class="bi bi-send"></i> {{ testing ? 'Envoi…' : 'Envoyer le test' }}
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
// Formulaire de configuration WhatsApp (WaaConnect), partagé par le backoffice
// (numéro dédié de la société) et le Landlord (plateforme / pour le compte d'une société).
export default {
  name: 'WhatsappSettingsForm',
  props: {
    http: { type: [Object, Function], required: true },   // instance axios (backoffice ou Landlord)
    url: { type: String, required: true },                 // GET / enregistrement ; test = url + '/test'
    method: { type: String, default: 'post' },
    title: { type: String, default: 'WhatsApp' },
    description: { type: String, default: '' },
    readonly: { type: Boolean, default: false },
  },
  emits: ['loaded'],
  data() {
    return {
      loaded: false,
      saving: false,
      testing: false,
      data: {},
      setting: {},
      form: { api_key: '', session_id: '', sender_phone: '', enabled: true },
      testForm: { phone_code: '+229', phone: '' },
    }
  },
  mounted() {
    this.load()
  },
  watch: {
    url() { this.load() },
  },
  computed: {
    health() {
      return this.setting?.health || null
    },
  },
  methods: {
    sessionLabel(status) {
      return { CONNECTED: 'connectée', NOT_FOUND: 'introuvable (identifiant incorrect ?)', UNREACHABLE: 'WaaConnect injoignable' }[status] || status
    },
    ago(iso) {
      const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
      if (minutes < 1) return "à l'instant"
      if (minutes < 60) return `il y a ${minutes} min`
      const hours = Math.round(minutes / 60)
      if (hours < 24) return `il y a ${hours} h`
      return `le ${new Date(iso).toLocaleDateString('fr-FR')}`
    },
    async load() {
      try {
        const { data } = await this.http.get(this.url)
        this.apply(data)
        this.$emit('loaded', data)
      } catch (e) {
        window.toastr['error'](e.response?.data?.message || 'Impossible de charger la configuration WhatsApp')
      }
    },
    apply(data) {
      this.data = data
      this.setting = data.setting || {}
      this.form = {
        api_key: '',
        session_id: this.setting.session_id || '',
        sender_phone: this.setting.sender_phone || '',
        enabled: this.setting.enabled !== false,
      }
      this.loaded = true
    },
    async save() {
      this.saving = true
      try {
        const { data } = await this.http[this.method](this.url, this.form)
        window.toastr['success'](data.message)
        this.setting = data.setting
        this.form.api_key = ''
      } catch (e) {
        const errors = e.response?.data?.errors
        window.toastr['error'](errors ? Object.values(errors)[0][0] : (e.response?.data?.message || 'Erreur'))
      }
      this.saving = false
    },
    async sendTest() {
      this.testing = true
      try {
        const { data } = await this.http.post(this.url + '/test', this.testForm)
        window.toastr['success'](data.message)
        this.setting = data.setting
      } catch (e) {
        const errors = e.response?.data?.errors
        window.toastr['error'](errors ? Object.values(errors)[0][0] : (e.response?.data?.message || 'Erreur'))
        if (e.response?.data?.setting) this.setting = e.response.data.setting
      }
      this.testing = false
    },
  },
}
</script>

<style scoped>
.wa-health__dot { flex-shrink: 0; width: 12px; height: 12px; margin-top: 5px; border-radius: 50%; background: #a1a5b7; }
.wa-health__dot.is-ok { background: #50cd89; box-shadow: 0 0 0 4px rgba(80, 205, 137, 0.2); }
.wa-health__dot.is-down { background: #f1416c; box-shadow: 0 0 0 4px rgba(241, 65, 108, 0.2); }
</style>
